// @ts-nocheck
/**
 * GA4 ecommerce dataLayer events.
 *
 * Reads the structured JSON emitted by snippets/gtm-data.liquid and pushes
 * standard GA4 ecommerce events to window.dataLayer so the GTM container
 * (GTM-TGQVF4R3, loaded in layout/theme.liquid) can fire analytics tags
 * without GTM-side changes.
 *
 * Page-load events:   view_item, view_cart, view_item_list, search
 * Interaction events: add_to_cart, select_item, sign_up
 *
 * Checkout funnel events (begin_checkout, add_shipping_info,
 * add_payment_info, purchase) are out of scope here — they require a
 * Custom Pixel in Shopify Admin -> Settings -> Customer Events.
 */
(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  /** @param {object} event */
  function push(event) {
    // Clear the previous ecommerce object so events don't bleed together.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(event);
  }

  /** Read and parse the embedded gtm-data JSON. */
  function readData() {
    var el = document.getElementById('gtm-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.error('[gtm-events] Failed to parse #gtm-data:', err);
      return null;
    }
  }

  var data = readData() || {};
  var currency = data.currency || 'USD';

  /**
   * Collects GA4 items from product-card[data-gtm-item] elements within a
   * root node, assigning a sequential index to each.
   * @param {ParentNode} [root]
   * @returns {Array<object>}
   */
  function collectCardItems(root) {
    root = root || document;
    var cards = root.querySelectorAll('product-card[data-gtm-item]');
    var items = [];
    for (var i = 0; i < cards.length; i++) {
      var item = parseCardItem(cards[i]);
      if (item) {
        item.index = items.length;
        items.push(item);
      }
    }
    return items;
  }

  /**
   * @param {Element} card
   * @returns {object|null}
   */
  function parseCardItem(card) {
    var raw = card.getAttribute('data-gtm-item');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (err) {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Lookup maps for resolving add_to_cart items by variant or product id.
  // ---------------------------------------------------------------------------

  var variantMap = data.variant_map || {};
  var cardByVariant = {};
  var cardByProduct = {};

  function indexCards() {
    var cards = document.querySelectorAll('product-card[data-gtm-item]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var item = parseCardItem(card);
      if (!item) continue;
      var variantId = card.getAttribute('data-gtm-variant-id');
      var productId = card.getAttribute('data-product-id');
      if (variantId) cardByVariant[String(variantId)] = item;
      if (productId) cardByProduct[String(productId)] = item;
    }
  }

  /**
   * Resolves a single GA4 item from a variant id (preferred) or product id.
   * @param {string|number|null} variantId
   * @param {string|number|null} productId
   * @returns {object|null}
   */
  function resolveItem(variantId, productId) {
    var vKey = variantId != null ? String(variantId) : null;
    var pKey = productId != null ? String(productId) : null;
    if (vKey && variantMap[vKey]) return Object.assign({}, variantMap[vKey]);
    if (vKey && cardByVariant[vKey]) return Object.assign({}, cardByVariant[vKey]);
    if (pKey && cardByProduct[pKey]) return Object.assign({}, cardByProduct[pKey]);
    return null;
  }

  // ---------------------------------------------------------------------------
  // Page-load events
  // ---------------------------------------------------------------------------

  function firePageLoadEvents() {
    if (data.view_item) {
      push({ event: 'view_item', ecommerce: data.view_item });
    }

    if (data.view_cart) {
      push({ event: 'view_cart', ecommerce: data.view_cart });
    }

    if (data.item_list) {
      var items = collectCardItems();
      if (items.length) {
        push({
          event: 'view_item_list',
          ecommerce: {
            item_list_id: data.item_list.item_list_id,
            item_list_name: data.item_list.item_list_name,
            items: items,
          },
        });
      }
    }

    if (data.search_term) {
      push({ event: 'search', search_term: data.search_term });
    }
  }

  // ---------------------------------------------------------------------------
  // select_item — click delegation on product cards
  // ---------------------------------------------------------------------------

  function onCardClick(event) {
    var card = event.target.closest('product-card[data-gtm-item]');
    if (!card) return;
    // Ignore add-to-cart / quick-add (those are add_to_cart) and the
    // "Learn More" quick-view trigger (opens a modal, not a selection).
    if (event.target.closest('[data-gc-buy-now], [data-gc-learn-more], .add-to-cart-button, button[name="add"]')) {
      return;
    }
    var item = parseCardItem(card);
    if (!item) return;

    var listContext = data.item_list || {};
    push({
      event: 'select_item',
      ecommerce: {
        item_list_id: listContext.item_list_id,
        item_list_name: listContext.item_list_name,
        items: [item],
      },
    });
  }

  // ---------------------------------------------------------------------------
  // add_to_cart — capture intent on click, fire on cart:update success
  // ---------------------------------------------------------------------------

  var pendingAdd = null;

  function captureAddIntent(event) {
    var trigger = event.target.closest(
      '[data-gc-buy-now], [ref="addToCartButton"], .add-to-cart-button, button[name="add"]'
    );
    if (!trigger) return;

    var variantId = null;
    var productId = null;
    var quantity = 1;

    // gc-* custom card / modal buttons carry the variant directly.
    if (trigger.hasAttribute('data-variant-id')) {
      variantId = trigger.getAttribute('data-variant-id');
      quantity = parseInt(trigger.getAttribute('data-gc-qty') || '1', 10) || 1;
    }

    // Standard Horizon product forms (PDP + quick-add) expose the selected
    // variant via the hidden id input within product-form-component.
    var form = trigger.closest('product-form-component');
    if (form) {
      if (!variantId) {
        var idInput = form.querySelector('input[name="id"]');
        if (idInput && idInput.value) variantId = idInput.value;
      }
      var qtyInput = form.querySelector('input[name="quantity"]');
      if (qtyInput && qtyInput.value) quantity = parseInt(qtyInput.value, 10) || quantity;
      productId = form.getAttribute('data-product-id');
    }

    if (!productId) {
      var card = trigger.closest('product-card[data-gtm-item]');
      if (card) productId = card.getAttribute('data-product-id');
    }

    pendingAdd = {
      variantId: variantId,
      productId: productId,
      quantity: quantity,
      at: Date.now(),
    };
  }

  function onCartUpdate(event) {
    var detail = event.detail && event.detail.data ? event.detail.data : {};
    if (detail.didError) return;

    // Source-agnostic: we treat this as an add only when a fresh add-to-cart
    // click was captured (pendingAdd). This covers standard product forms,
    // quick-add, and the custom gc-buy-now buttons regardless of the `source`
    // they dispatch — while cart line qty edits / removals, which never set
    // pendingAdd, are correctly ignored. Any future add control just needs to
    // match the selector in captureAddIntent().
    if (!pendingAdd || Date.now() - pendingAdd.at > 5000) {
      pendingAdd = null;
      return;
    }

    // Use the quantity captured from the click. detail.itemCount is
    // unreliable here: the gc-buy-now flow reports the cart's *total*
    // item count rather than the amount just added.
    var qty = pendingAdd.quantity || 1;
    var item = resolveItem(detail.variantId || pendingAdd.variantId, pendingAdd.productId || detail.productId);
    pendingAdd = null;
    if (!item) return;

    item.quantity = qty;

    push({
      event: 'add_to_cart',
      ecommerce: {
        currency: currency,
        value: (parseFloat(item.price) || 0) * item.quantity,
        items: [item],
      },
    });
  }

  // ---------------------------------------------------------------------------
  // sign_up — native Shopify customer forms reload on success and render a
  // success marker. Detect it on load.
  // ---------------------------------------------------------------------------

  function fireSignUp() {
    var success = document.querySelector(
      '[id^="Email-signup__message-success"], .newsletter-bar__success'
    );
    if (success) {
      push({ event: 'sign_up', method: 'email' });
    }
  }

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  function init() {
    indexCards();
    firePageLoadEvents();
    fireSignUp();

    document.addEventListener('click', onCardClick, true);
    document.addEventListener('click', captureAddIntent, true);
    document.addEventListener('cart:update', onCartUpdate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
